/**
 * The last suggestions: every one starts kept; untick what you do not want, then Apply (one
 * undoable edit). Applied suggestions leave the list; the rest stay until Dismiss or the next run.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { suggestionCount } from "../../state/ai";
import { CategoriesRow, FindingRow, GroupRow, KindRow, LinkRow, NewNodeRow, NoteRow } from "./SuggestionRows";

/** What the apply button does for each task. */
const APPLY_HINT = {
  notes: "Kept descriptions become the nodes' notes.",
  groups: "Kept groups are created, with their members moved in.",
  review: "Kept findings become comments on their nodes.",
  enrich: "Kept suggestions are added to the scanned graph: new nodes, notes, groups, categories, kinds and links.",
} as const;

/** The suggestions and their buttons (nothing when there are none). */
export function SuggestionsSection(): JSX.Element {
  const { ai } = useApp();
  return (
    <Show when={ai.last()}>
      {(s) => {
        const count = () => suggestionCount(s());
        /** A node's name: the graph's, or a suggested new node's. */
        const names = (id: string) => s().nodes.find((n) => n.node.id === id)?.node.name;
        return (
          <section class="grp" id="ai-suggestions" data-task={s().task}>
            <h3>
              Suggestions <span id="ai-count">{count()}</span>
            </h3>
            <p class="small muted">
              {s().model} · {(s().ms / 1000).toFixed(1)} s · {APPLY_HINT[s().task]}
            </p>
            <Show when={count()} fallback={<p class="small muted">Nothing (left) to suggest.</p>}>
              <div class="btnrow">
                <button type="button" class="btn primary" id="ai-apply" disabled={!ai.keptCount()} onClick={() => void ai.apply()}>
                  Apply {ai.keptCount()}
                </button>
                <button type="button" class="btn" id="ai-keep-all" onClick={() => ai.setAllKept(true)}>
                  All
                </button>
                <button type="button" class="btn" id="ai-keep-none" onClick={() => ai.setAllKept(false)}>
                  None
                </button>
                <button type="button" class="btn" id="ai-dismiss" onClick={() => ai.dismiss()}>
                  Dismiss
                </button>
              </div>
            </Show>
            <ul class="ai-list">
              <For each={s().nodes}>{(n) => <NewNodeRow node={n} />}</For>
              <For each={s().notes}>{(n) => <NoteRow note={n} />}</For>
              <For each={s().groups}>{(g) => <GroupRow group={g} />}</For>
              <For each={s().categories}>{(c) => <CategoriesRow categories={c} />}</For>
              <For each={s().kinds}>{(k) => <KindRow kind={k} />}</For>
              <For each={s().links}>{(l) => <LinkRow link={l} names={names} />}</For>
              <For each={s().findings}>{(f, i) => <FindingRow finding={f} index={i()} />}</For>
            </ul>
            <Show when={s().warnings.length}>
              <ul class="small muted ai-warnings" id="ai-warnings">
                <For each={s().warnings}>{(w) => <li>! {w}</li>}</For>
              </ul>
            </Show>
          </section>
        );
      }}
    </Show>
  );
}
